/* /app/frontend/src/components/dealroom/MortgageMilestoneModal.jsx
 *
 * iter-399 · P0 · Mortgage milestone capture modal.
 *
 * Four modes — one lightweight modal per substep.  Each mode
 * posts to its own endpoint and returns the fresh mortgage state
 * from the server so the parent Deal Room can re-render the NBA,
 * Deal Health and timeline from SSOT.
 *
 *   mode="app_submitted"      → submitted_on
 *   mode="valuation_booked"   → booked_on + optional time + access_notes
 *   mode="valuation_outcome"  → outcome radios + optional notes
 *   mode="offer_received"     → received_on + optional expires_on
 */
import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../../lib/api";


const MODE_META = {
  app_submitted:      { title: "Mortgage application submitted",  endpoint: "application-submitted" },
  valuation_booked:   { title: "Book mortgage valuation",         endpoint: "valuation-booked" },
  valuation_outcome:  { title: "Record valuation outcome",        endpoint: "valuation-outcome" },
  offer_received:     { title: "Mortgage offer received",         endpoint: "offer-received" },
};


const OUTCOMES = [
  { key: "passed",           label: "Passed",                sub: "Full offer expected" },
  { key: "down_valuation",   label: "Down Valuation",        sub: "Deal Health → Watch" },
  { key: "failed_retention", label: "Failed / Retention",    sub: "Deal Health → At Risk" },
  { key: "awaiting",         label: "Awaiting Result",       sub: "Log and re-visit" },
];


function _todayIso() {
  return new Date().toISOString().slice(0, 10);
}


export default function MortgageMilestoneModal({ dealId, mode, initialState, onClose, onCompleted }) {
  const meta = MODE_META[mode];
  const [submittedOn, setSubmittedOn] = useState(_todayIso());
  const [bookedOn, setBookedOn] = useState(_todayIso());
  const [bookedTime, setBookedTime] = useState("");
  const [accessNotes, setAccessNotes] = useState("");
  const [outcome, setOutcome] = useState("passed");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [receivedOn, setReceivedOn] = useState(_todayIso());
  const [expiresOn, setExpiresOn] = useState("");
  const [saving, setSaving] = useState(false);

  // iter-398B — Modal-hardening: portal + Escape + body scroll lock.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !saving) onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [saving, onClose]);

  // Prime fields from prior captures so the operator can amend rather
  // than re-key.  `latest row wins` on the backend so re-submitting
  // is safe + expected when a booking slips.
  useEffect(() => {
    if (!initialState) return;
    const substeps = initialState.substeps || {};
    if (mode === "app_submitted" && substeps.application_submitted?.submitted_on) {
      setSubmittedOn(substeps.application_submitted.submitted_on);
    }
    if (mode === "valuation_booked") {
      const v = substeps.valuation_booked;
      if (v?.booked_on) setBookedOn(v.booked_on);
      if (v?.time) setBookedTime(v.time);
      if (v?.access_notes) setAccessNotes(v.access_notes);
    }
    if (mode === "valuation_outcome") {
      const o = substeps.valuation_outcome;
      if (o?.outcome) setOutcome(o.outcome);
      if (o?.notes) setOutcomeNotes(o.notes);
    }
    if (mode === "offer_received") {
      const off = substeps.offer_received;
      if (off?.received_on) setReceivedOn(off.received_on);
      if (off?.expires_on) setExpiresOn(off.expires_on);
    }
  }, [mode, initialState]);

  const submit = useCallback(async () => {
    setSaving(true);
    try {
      let payload = {};
      if (mode === "app_submitted")     payload = { submitted_on: submittedOn };
      if (mode === "valuation_booked")  payload = { booked_on: bookedOn, time: bookedTime || null, access_notes: accessNotes || null };
      if (mode === "valuation_outcome") payload = { outcome, notes: outcomeNotes || null };
      if (mode === "offer_received")    payload = { received_on: receivedOn, expires_on: expiresOn || null };
      const { data } = await api.post(`/api/v2/deals/${dealId}/mortgage/${meta.endpoint}`, payload);
      toast.success(`${meta.title} · saved`);
      onCompleted?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  }, [dealId, mode, meta, submittedOn, bookedOn, bookedTime, accessNotes, outcome, outcomeNotes, receivedOn, expiresOn, onCompleted]);

  if (!mode || !meta) return null;
  const node = (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/45 p-4" onClick={saving ? undefined : onClose} data-testid="mortgage-modal-overlay" style={{ overscrollBehavior: "contain" }}>
      <div
        className="relative w-full max-w-md max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={meta.title}
        data-testid="mortgage-modal"
      >
        <header className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-white">
          <h3 className="text-[14.5px] font-bold text-slate-900">{meta.title}</h3>
          <button type="button" onClick={onClose} disabled={saving} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40" data-testid="mortgage-modal-close" aria-label="Close">
            <X size={16} strokeWidth={2.4} />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-3">
          {mode === "app_submitted" && (
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Submission date</span>
              <input type="date" value={submittedOn} onChange={(e) => setSubmittedOn(e.target.value)}
                className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                data-testid="mortgage-app-submitted-date"
              />
            </label>
          )}
          {mode === "valuation_booked" && (
            <>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Valuation date</span>
                <input type="date" value={bookedOn} onChange={(e) => setBookedOn(e.target.value)}
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-valuation-date"
                />
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Time <span className="text-slate-400 font-normal">(optional)</span></span>
                <input type="text" value={bookedTime} onChange={(e) => setBookedTime(e.target.value)} placeholder="e.g. 10:30"
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-valuation-time"
                />
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Access arrangements <span className="text-slate-400 font-normal">(optional)</span></span>
                <textarea rows={3} value={accessNotes} onChange={(e) => setAccessNotes(e.target.value)}
                  placeholder="Contact vendor · key at agent office · etc."
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-valuation-access"
                />
              </label>
            </>
          )}
          {mode === "valuation_outcome" && (
            <>
              <div className="space-y-1.5" role="radiogroup" data-testid="mortgage-outcome-radios">
                {OUTCOMES.map((opt) => (
                  <label key={opt.key} className={`flex items-start gap-2 p-2.5 rounded-lg ring-1 cursor-pointer transition ${outcome === opt.key ? "ring-violet-400 bg-violet-50" : "ring-slate-200 hover:bg-slate-50"}`}>
                    <input type="radio" name="mortgage-outcome" checked={outcome === opt.key} onChange={() => setOutcome(opt.key)}
                      className="mt-0.5 text-violet-600 focus:ring-violet-300"
                      data-testid={`mortgage-outcome-${opt.key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900">{opt.label}</div>
                      <div className="text-[11.5px] text-slate-500">{opt.sub}</div>
                    </div>
                  </label>
                ))}
              </div>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Notes <span className="text-slate-400 font-normal">(optional)</span></span>
                <textarea rows={3} value={outcomeNotes} onChange={(e) => setOutcomeNotes(e.target.value)}
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-outcome-notes"
                />
              </label>
            </>
          )}
          {mode === "offer_received" && (
            <>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Offer received on</span>
                <input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)}
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-offer-received-date"
                />
              </label>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500 mb-1">Offer expires on <span className="text-slate-400 font-normal">(optional)</span></span>
                <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)}
                  className="w-full rounded-md ring-1 ring-slate-200 bg-white px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  data-testid="mortgage-offer-expires-date"
                />
              </label>
            </>
          )}
        </div>
        <footer className="shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-md text-[12.5px] font-semibold text-slate-600 hover:bg-slate-200" data-testid="mortgage-modal-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
            data-testid="mortgage-modal-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} strokeWidth={2.4} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
